import { useState, useEffect } from "react";
import { http, inr, fmtErr } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { GuestDetailDialog, StayDetailDialog, BillDetailDialog, OrderDetailDialog } from "./records";
import ResponsibleManagerCard from "@/pages/admin/ResponsibleManagerCard";
import { AddFoodOrderDialog, AddServiceRequestDialog } from "./OrderEntryDialogs";

export function StaysTable({ stays, onChanged }) {
  const [coTarget, setCoTarget] = useState(null);
  const [viewStay, setViewStay] = useState(null);
  const [busy, setBusy] = useState(false);

  const doCheckout = async () => {
    setBusy(true);
    try {
      await http.post(`/admin/stays/${coTarget.id}/checkout`);
      toast.success("Guest checked out");
      setCoTarget(null);
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}><th className="p-2">Guest</th><th>Manager</th><th>Dates</th><th>Pax</th><th>Status</th><th>Balance</th><th></th></tr></thead>
        <tbody>
          {stays.map((s) => (
            <tr key={s.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`stay-${s.id}`}>
              <td className="p-2 font-medium">{s.guest?.full_name}</td>
              <td className="text-xs" data-testid={`stay-manager-${s.id}`}>{s.responsible_manager?.name || <span style={{ color: "var(--terracotta)" }}>UNASSIGNED</span>}</td>
              <td>{s.check_in_date} → {s.check_out_date}</td>
              <td>{s.total_pax}</td>
              <td>
                <span className={s.status === "CHECKED_IN" ? "badge-pine" : "badge-terracotta"}>{s.status}</span>
                {s.checkout_overdue && <span className="badge-terracotta ml-2" data-testid={`stay-overdue-${s.id}`}>CHECKOUT OVERDUE</span>}
              </td>
              <td>{inr(s.bill.balance_due)}</td>
              <td className="whitespace-nowrap">
                <button data-testid={`stay-view-${s.id}`} onClick={() => setViewStay(s.id)} className="btn-outline" style={{ padding: "6px 12px", fontSize: 13 }}>View</button>
                {s.status === "CHECKED_IN" && (
                  <button data-testid={`stay-checkout-${s.id}`} onClick={() => setCoTarget(s)} className="btn-outline ml-1" style={{ padding: "6px 12px", fontSize: 13 }}>Check Out</button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {viewStay && <StayDetailDialog stayId={viewStay} onClose={() => setViewStay(null)} onChanged={onChanged} />}

      {coTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid="checkout-dialog">
          <div className="card max-w-md w-full">
            <h3 className="font-serif text-2xl mb-4">Check Out Guest</h3>
            <div className="space-y-2 text-sm mb-4">
              <CoRow label="Guest" value={coTarget.guest?.full_name} />
              <CoRow label="Booking ID" value={coTarget.booking_reference || "—"} />
              <CoRow label="Room(s)" value={(coTarget.room_names || []).join(", ") || "—"} />
              <CoRow label="Check-in" value={coTarget.check_in_date} />
              <CoRow label="Scheduled checkout" value={coTarget.check_out_date} />
              <CoRow label="Outstanding balance" value={inr(coTarget.bill?.balance_due)} accent />
            </div>
            <p className="text-xs mb-4" style={{ color: "var(--muted)" }}>
              This closes the operational stay and records a manual checkout by you. All bills, food/service orders and payment history are preserved.
            </p>
            <div className="flex gap-3">
              <button data-testid="checkout-confirm" onClick={doCheckout} disabled={busy} className="btn-primary flex-1">{busy ? "Processing…" : "Confirm Checkout"}</button>
              <button data-testid="checkout-cancel" onClick={() => setCoTarget(null)} className="btn-outline flex-1">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function CoRow({ label, value, accent }) {
  return (
    <div className="flex justify-between">
      <span style={{ color: "var(--muted)" }}>{label}</span>
      <span className="font-medium" style={{ color: accent ? "var(--terracotta)" : "var(--fg)" }}>{value}</span>
    </div>
  );
}

export function GuestsTable({ guests, onChanged }) {
  const [sel, setSel] = useState(null);
  return (
    <div className="card overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}><th className="p-2">Name</th><th>Mobile</th><th>Total stays</th><th>Nights</th><th>Total spend</th><th>Consent</th></tr></thead>
        <tbody>
          {guests.map((g) => (
            <tr key={g.id} className="border-b cursor-pointer hover:bg-black/[.02]" style={{ borderColor: "var(--border)" }} data-testid={`guest-row-${g.id}`} onClick={() => setSel(g)}>
              <td className="p-2 font-medium" style={{ color: "var(--pine)", textDecoration: "underline" }}>{g.full_name}</td><td>{g.mobile}</td><td>{g.total_stays}</td><td>{g.total_nights}</td><td>{inr(g.total_spend)}</td><td>{g.marketing_consent ? "Yes" : "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {sel && <GuestDetailDialog guest={sel} onClose={() => setSel(null)} onChanged={onChanged} />}
    </div>
  );
}

const ORDER_STATUSES = [["NEW", "New"], ["ACKNOWLEDGED", "Acknowledged"], ["PREPARING", "Preparing"], ["READY", "Ready"], ["DELIVERED", "Delivered"], ["CANCELLED", "Cancelled"]];

export function OrdersTable({ orders, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const canEnter = isOwner || user?.role === "MANAGER";
  const [sel, setSel] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const setStatus = async (o, status) => {
    let reason = "";
    if (status === "CANCELLED") {
      if ((o.status === "PREPARING" || o.status === "READY") &&
          !window.confirm(`Order ${o.order_number} is already ${o.status}. Cancel anyway? The unpaid charge will be removed from the bill.`)) return;
      const r = window.prompt("Cancellation reason (optional):");
      if (r === null) return;
      reason = r;
    }
    try {
      await http.put(`/admin/food-orders/${o.id}/status`, { status, reason });
      toast.success("Order updated");
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await http.delete(`/admin/food-orders/${delTarget.id}`);
      toast.success("Test order deleted");
      setDelTarget(null);
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card overflow-x-auto">
      {canEnter && (
        <div className="flex justify-end mb-3">
          <button data-testid="add-food-order-btn" onClick={() => setAddOpen(true)} className="btn-primary" style={{ padding: "8px 16px", fontSize: 13 }}>+ Add Food Order</button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}><th className="p-2">Order</th><th>Guest</th><th>Items</th><th>Total</th><th>Status</th><th>Updated by</th><th>Assigned</th><th></th></tr></thead>
        <tbody>
          {orders.map((o) => {
            const terminal = o.status === "DELIVERED" || o.status === "CANCELLED";
            return (
            <tr key={o.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`admin-order-${o.order_number}`}>
              <td className="p-2 font-medium">{o.order_number}</td><td>{o.guest_name}</td><td>{o.items.length}</td><td>{inr(o.total)}</td>
              <td>
                <select data-testid={`order-status-${o.id}`} value={o.status} disabled={terminal}
                  onChange={(e) => setStatus(o, e.target.value)}
                  style={{ padding: "4px 8px", fontSize: 12, width: "auto", opacity: terminal ? 0.7 : 1 }}>
                  {ORDER_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </td>
              <td className="text-xs" style={{ color: "var(--muted)" }}>{o.acknowledged_by || (o.status_history?.length ? o.status_history[o.status_history.length - 1].by : "—")}</td>
              <td><AssignCell id={o.id} type="orders" value={o.assigned_to} onChanged={onChanged} /></td>
              <td className="whitespace-nowrap">
                <button data-testid={`order-view-${o.id}`} onClick={() => setSel(o)} className="btn-outline" style={{ padding: "4px 10px", fontSize: 12 }}>View</button>
                {isOwner && !terminal && (
                  <button data-testid={`order-delete-${o.id}`} onClick={() => setDelTarget(o)} className="btn-outline ml-1" style={{ padding: "4px 10px", fontSize: 12, color: "var(--terracotta)" }}>Delete</button>
                )}
              </td>
            </tr>
            );
          })}
          {orders.length === 0 && <tr><td colSpan="8" className="p-4 text-sm" style={{ color: "var(--muted)" }}>No food orders yet.</td></tr>}
        </tbody>
      </table>

      {sel && <OrderDetailDialog order={sel} onClose={() => setSel(null)} onChanged={onChanged} />}
      {addOpen && <AddFoodOrderDialog onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); onChanged && onChanged(); }} />}

      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="order-delete-dialog">
          <div className="card max-w-md w-full">
            <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--terracotta)" }}>Permanently delete this test order?</h3>
            <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
              Order {delTarget.order_number} ({inr(delTarget.total)}) will be permanently removed. Delivered orders or stays with verified payments are protected automatically.
            </p>
            <div className="flex gap-3">
              <button data-testid="order-delete-confirm" onClick={doDelete} disabled={busy} className="btn-primary flex-1" style={{ background: "var(--terracotta)" }}>{busy ? "Deleting…" : "Yes, Delete"}</button>
              <button data-testid="order-delete-abort" onClick={() => setDelTarget(null)} className="btn-outline flex-1">Keep</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const SVC_STATUSES = [["REQUESTED", "Requested"], ["ACCEPTED", "Acknowledged"], ["IN_PROGRESS", "In Progress"], ["COMPLETED", "Completed"], ["CANCELLED", "Cancelled"]];

export function ServicesTable({ reqs, onChanged }) {
  const { user } = useAuth();
  const isOwner = user?.role === "SUPER_ADMIN";
  const canEnter = isOwner || user?.role === "MANAGER";
  const [detail, setDetail] = useState(null);
  const [delTarget, setDelTarget] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const setStatus = async (r, status) => {
    try {
      await http.put(`/admin/service-requests/${r.id}/status`, { status });
      toast.success("Status updated");
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const changeQty = async (r, val) => {
    const qty = parseInt(val);
    if (isNaN(qty) || qty < 1 || qty === r.quantity) return;
    try {
      await http.put(`/admin/service-requests/${r.id}/quantity`, { quantity: qty });
      toast.success("Quantity updated — bill recalculated at the original price");
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await http.delete(`/admin/service-requests/${delTarget.id}`);
      toast.success("Test service request deleted");
      setDelTarget(null);
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="card overflow-x-auto">
      {canEnter && (
        <div className="flex justify-end mb-3">
          <button data-testid="add-service-request-btn" onClick={() => setAddOpen(true)} className="btn-primary" style={{ padding: "8px 16px", fontSize: 13 }}>+ Add Service Request</button>
        </div>
      )}
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}><th className="p-2">Service</th><th>Guest</th><th>Qty</th><th>Unit</th><th>Total</th><th>Status</th><th>Assigned</th><th></th></tr></thead>
        <tbody>
          {reqs.map((r) => {
            const terminal = r.status === "COMPLETED" || r.status === "CANCELLED";
            const qtyEditable = isOwner && (r.status === "REQUESTED" || r.status === "ACCEPTED");
            return (
            <tr key={r.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`admin-svc-${r.id}`}>
              <td className="p-2 font-medium">{r.service_name_snapshot}{r.special_instruction && <div className="text-xs font-normal" style={{ color: "var(--muted)" }}>{r.special_instruction}</div>}</td>
              <td>{r.guest_name}</td>
              <td>
                {qtyEditable ? (
                  <input data-testid={`svc-qty-${r.id}`} type="number" min="1" max="20" defaultValue={r.quantity} key={`q-${r.id}-${r.quantity}`}
                    onBlur={(e) => changeQty(r, e.target.value)} style={{ width: 60, padding: "4px 8px" }} />
                ) : r.quantity}
              </td>
              <td className="text-xs" style={{ color: "var(--muted)" }}>{inr(r.unit_price)}</td>
              <td>{inr(r.total)}</td>
              <td>
                <select data-testid={`svc-status-${r.id}`} value={r.status} disabled={terminal}
                  onChange={(e) => setStatus(r, e.target.value)}
                  className={terminal ? "" : "cursor-pointer"}
                  style={{ padding: "4px 8px", fontSize: 12, width: "auto", opacity: terminal ? 0.7 : 1 }}>
                  {SVC_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </td>
              <td><AssignCell id={r.id} type="service-requests" value={r.assigned_to} onChanged={onChanged} /></td>
              <td className="whitespace-nowrap">
                <button data-testid={`svc-view-${r.id}`} onClick={() => setDetail(r)} className="btn-outline" style={{ padding: "4px 10px", fontSize: 12 }}>View</button>
                {isOwner && !terminal && (
                  <button data-testid={`svc-delete-${r.id}`} onClick={() => setDelTarget(r)} className="btn-outline ml-1" style={{ padding: "4px 10px", fontSize: 12, color: "var(--terracotta)" }}>Delete</button>
                )}
              </td>
            </tr>
            );
          })}
          {reqs.length === 0 && <tr><td colSpan="8" className="p-4 text-sm" style={{ color: "var(--muted)" }}>No service requests yet.</td></tr>}
        </tbody>
      </table>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.45)" }} data-testid="svc-detail-dialog">
          <div className="card max-w-md w-full" style={{ maxHeight: "90vh", overflowY: "auto" }}>
            <h3 className="font-serif text-2xl mb-4">{detail.service_name_snapshot}</h3>
            <div className="space-y-2 text-sm mb-4">
              <CoRow label="Guest" value={detail.guest_name} />
              <CoRow label="Status" value={detail.status} />
              <CoRow label="Quantity" value={detail.quantity} />
              <CoRow label="Unit price (snapshot)" value={inr(detail.unit_price)} />
              <CoRow label={`Tax (${detail.tax_rate || 0}%)`} value={inr(detail.tax || 0)} />
              <CoRow label="Total" value={inr(detail.total)} accent />
              {detail.special_instruction && <CoRow label="Instruction" value={detail.special_instruction} />}
              {detail.assigned_to && <CoRow label="Assigned to" value={detail.assigned_to} />}
              <CoRow label="Requested" value={detail.requested_at ? new Date(detail.requested_at).toLocaleString() : "—"} />
              {detail.cancelled_by && <CoRow label="Cancelled by" value={detail.cancelled_by} />}
            </div>
            {Array.isArray(detail.status_history) && detail.status_history.length > 0 && (
              <div className="mb-4">
                <div className="eyebrow mb-2">History</div>
                {detail.status_history.map((h, i) => (
                  <div key={i} className="text-xs py-1" style={{ color: "var(--muted)" }}>{h.status} · {h.by} · {new Date(h.at).toLocaleString()}</div>
                ))}
              </div>
            )}
            <button data-testid="svc-detail-close" onClick={() => setDetail(null)} className="btn-outline w-full">Close</button>
          </div>
        </div>
      )}

      {addOpen && <AddServiceRequestDialog onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); onChanged && onChanged(); }} />}

      {delTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,.5)" }} data-testid="svc-delete-dialog">
          <div className="card max-w-md w-full">
            <h3 className="font-serif text-2xl mb-2" style={{ color: "var(--terracotta)" }}>Permanently delete this test request?</h3>
            <p className="text-sm mb-4" style={{ color: "var(--muted)" }}>
              "{delTarget.service_name_snapshot}" × {delTarget.quantity} for {delTarget.guest_name} will be permanently removed. If it has billing/payment history the system will refuse and ask you to Cancel instead.
            </p>
            <div className="flex gap-3">
              <button data-testid="svc-delete-confirm" onClick={doDelete} disabled={busy} className="btn-primary flex-1" style={{ background: "var(--terracotta)" }}>{busy ? "Deleting…" : "Yes, Delete"}</button>
              <button data-testid="svc-delete-abort" onClick={() => setDelTarget(null)} className="btn-outline flex-1">Keep</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export function BillsTable({ stays, onChanged }) {
  const [sel, setSel] = useState(null);
  return (
    <div className="card overflow-x-auto" data-testid="bills-tab">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}><th className="p-2">Stay / Booking</th><th>Guest</th><th>Total</th><th>Paid</th><th>Balance</th><th>Status</th></tr></thead>
        <tbody>
          {stays.map((s) => (
            <tr key={s.id} className="border-b cursor-pointer hover:bg-black/[.02]" style={{ borderColor: "var(--border)" }} data-testid={`bill-row-${s.id}`} onClick={() => setSel(s.id)}>
              <td className="p-2 font-medium" style={{ color: "var(--pine)", textDecoration: "underline" }}>{s.booking_reference || s.check_in_date}</td>
              <td>{s.guest?.full_name}</td>
              <td>{inr(s.bill.grand_total)}</td>
              <td>{inr(s.bill.amount_paid)}</td>
              <td style={{ color: s.bill.balance_due > 0 ? "var(--terracotta)" : "var(--pine)" }}>{inr(s.bill.balance_due)}</td>
              <td><span className={s.status === "CHECKED_IN" ? "badge-pine" : "badge-terracotta"}>{s.status}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      {sel && <BillDetailDialog stayId={sel} onClose={() => setSel(null)} onChanged={onChanged} />}
    </div>
  );
}

export function StaffTable({ staffList }) {
  return (
    <div className="card" data-testid="staff-tab">
      <h3 className="font-serif text-2xl mb-3">Team Access</h3>
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b" style={{ borderColor: "var(--border)" }}><th className="p-2">Name</th><th>Email</th><th>Role</th></tr></thead>
        <tbody>
          {staffList.map((u) => (
            <tr key={u.id} className="border-b" style={{ borderColor: "var(--border)" }} data-testid={`staff-row-${u.id}`}>
              <td className="p-2 font-medium">{u.name}</td><td>{u.email}</td><td><span className="badge-pine">{u.role}</span></td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-xs mt-4" style={{ color: "var(--muted)" }}>
        Staff sign in at /login and land on /staff (operational only). They cannot change prices, verify payments or edit website content — enforced in the backend.
      </p>
    </div>
  );
}

function AssignCell({ id, type, value, onChanged }) {
  const [staff, setStaff] = useState([]);
  useEffect(() => { http.get("/admin/staff/assignable").then((r) => setStaff(r.data.staff || [])).catch(() => {}); }, []);
  const assign = async (name) => {
    try {
      await http.post(`/admin/${type}/${id}/assign`, { staff_name: name });
      toast.success(name ? `Assigned to ${name}` : "Unassigned");
      onChanged && onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
  };
  return (
    <select data-testid={`assign-${type}-${id}`} value={value || ""} onChange={(e) => assign(e.target.value)} style={{ padding: "4px 8px", fontSize: 12, width: "auto" }}>
      <option value="">Unassigned</option>
      {staff.map((s) => <option key={s.id} value={s.name}>{s.name}</option>)}
    </select>
  );
}
